import Avo from 'analytics/Avo';
import Button from 'components/atoms/Button';
import Footer from 'components/molecules/Footer';
import { BUTTON_NAMES } from 'constant/button-names';
import { useConfig } from 'context/app-config';
import { selectOrderServices, selectSelectedServices } from 'pages/newOrder/newOrder.selectors';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectCustomer } from 'redux-stores/slices/authSlice';
import styled from 'styled-components';

import PricingButton from './PricingButton';

const StyledContainer = styled.div`
  display: flex;
`;

const StyledPlaceNewOrderListButton = styled(Button)`
  background-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.backgroundColor};
  border-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.borderColor};
  color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.textColor};
  font-size: 1rem;
  font-style: normal;
  line-height: 24px;
  font-weight: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.fontWeight ?? '700'};
  &:hover,
  &:focus,
  &:active,
  &.btn-primary:not(:disabled):not(.disabled):active {
    background-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.backgroundColor};
    border-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.borderColor};
    color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.textColor};
  }

  @media (max-width: 444px) {
    font-size: 0.9rem;
  }
  @media (max-width: 359px) {
    font-size: 0.79rem;
  }
  @media (max-width: 330px) {
    font-size: 0.83rem;
  }
  @media (max-width: 290px) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    height: 40px;
    font-size: 0.69rem;
  }
  @media (max-width: ${window.innerWidth < 290 ? 289 : 290}px) {
    height: 40px;
    padding-left: 0.15rem;
    padding-right: 0.15rem;
    font-size: 0.59rem;
  }
`;

interface Props {
  hidePriceButton?: boolean;
  isVisible?: boolean;
  primaryButtonIcon?: JSX.Element;
  onContinue?: () => void;
  isLoading?: boolean;
}

const HomeFooter: React.FC<Props> = ({ hidePriceButton, isVisible = true, primaryButtonIcon, onContinue, isLoading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const customer = useSelector(selectCustomer);
  const config = useConfig();
  const selectedServiceLines = useSelector(selectSelectedServices);
  const hasSupportForMultipleDropoffs = useMemo(() => config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, [config]);
  const services = useSelector(selectOrderServices);

  // handle action when clicking on new order
  const handleNewOrder = useCallback(() => {
    navigate(`/newOrder/flex`);
    if (customer?.id) {
      Avo.orderStarted({
        screen: 'home Screen Footer',
        serviceLinesSelected: hasSupportForMultipleDropoffs ? selectedServiceLines : ['laundry'],
      });
    }
  }, [hasSupportForMultipleDropoffs, navigate, selectedServiceLines, customer?.id]);

  const isDisabled = useMemo(
    () => config?.showSelectServicesOnHomePage && services.length === 0,
    [config?.showSelectServicesOnHomePage, services.length]
  );

  return (
    <Footer isVisible={isVisible}>
      <StyledContainer>
        {!hidePriceButton && <PricingButton />}

        <StyledPlaceNewOrderListButton
          block
          size="lg"
          onClick={isLoading ? undefined : onContinue ?? handleNewOrder}
          disabled={isDisabled}
          isLoading={isLoading}
          className="d-flex justify-content-center align-items-center pr-2 py-2"
          buttonName={BUTTON_NAMES.PLACE_NEW_ORDER}
        >
          {t('homePage.placeNewOrder')}
          {primaryButtonIcon}
        </StyledPlaceNewOrderListButton>
      </StyledContainer>
    </Footer>
  );
};

export default HomeFooter;
